import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

export function useAdminData({ activeAccount, apiRootUrl, getAccessToken }) {

    // Admin status and editable admin tables
    const [isAdmin, setIsAdmin] = useState(false);
    const [adminToken, setAdminToken] = useState(null);
    const [users, setUsers] = useState([]);
    const [distributions, setDistributions] = useState([]);

    // Load users. Success means the current user has admin access
    const fetchUsers = useCallback(async () => {
        if (!activeAccount) return;

        try {
            const accessToken = await getAccessToken();

            const response = await axios.get(`${apiRootUrl}/v1/users`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            setUsers(response.data || []);
            setIsAdmin(true);
            setAdminToken(accessToken);
        } catch (err) {
            console.error('Error loading users', err);

            if (err.response && [401, 403].includes(err.response.status)) {
                setIsAdmin(false);
                setAdminToken(null);
            }

            setUsers([]);
        }
    }, [activeAccount, apiRootUrl, getAccessToken]);

    // Create or update a user row.
    const saveUser = useCallback(async (u) => {
        if (!activeAccount) return;

        try {
            const accessToken = await getAccessToken();

            if (u.isNew) {
                await axios.post(`${apiRootUrl}/v1/users`, {
                    userId: u.user_id,
                    recipient: {
                        userName: u.user_name,
                        emailAddress: u.email,
                        distribution: u.distribution,
                    },
                    admin: !!u.admin,
                }, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
            } else {
                await axios.put(`${apiRootUrl}/v1/users/${u.user_id}`, {
                    updates: {
                        user_name: u.user_name,
                        email: u.email,
                        distribution: u.distribution,
                        admin: !!u.admin,
                    },
                }, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
            }

            await fetchUsers();
        } catch (err) {
            console.error('Error saving user', err);
        }
    }, [activeAccount, apiRootUrl, fetchUsers, getAccessToken]);

    // Delete a user, then refresh the user list
    const deleteUser = useCallback(async (u) => {
        if (!activeAccount) return;

        try {
            const accessToken = await getAccessToken();

            await axios.delete(`${apiRootUrl}/v1/users/${u.user_id}`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            await fetchUsers();
        } catch (err) {
            console.error('Error deleting user', err);
        }
    }, [activeAccount, apiRootUrl, fetchUsers, getAccessToken]);

    // Load distribution/entity mappings for the admin screen
    const fetchDistributions = useCallback(async () => {
        if (!activeAccount) return;

        try {
            const accessToken = await getAccessToken();

            const response = await axios.get(`${apiRootUrl}/v1/distributions`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            const data = response.data || [];
            const top = data.slice(0, 5);
            const rest = data.slice(5);

            // Preserve the first five rows, then sort the rest
            rest.sort((a, b) => {
                if (a.distribution !== b.distribution) {
                    return a.distribution.localeCompare(b.distribution);
                }

                return a.entity.localeCompare(b.entity);
            });

            setDistributions([...top, ...rest]);
        } catch (err) {
            console.error('Error loading distributions', err);
            setDistributions([]);
        }
    }, [activeAccount, apiRootUrl, getAccessToken]);

    // Create or update a distribution row
    const saveDist = useCallback(async (d) => {
        if (!activeAccount) return;

        try {
            const accessToken = await getAccessToken();

            if (d.isNew) {
                await axios.post(`${apiRootUrl}/v1/distributions`, {
                    distribution: d.distribution,
                    entity: d.entity,
                    dispName: d.disp_name,
                    category: d.category,
                }, {
                    headers: { Authorization: `Bearer ${accessToken}` },
                });
            } else {
                await axios.put(
                    `${apiRootUrl}/v1/distributions/${d.originalDistribution}/${d.originalEntity}`,
                    {
                        updates: {
                            distribution: d.distribution,
                            entity: d.entity,
                            disp_name: d.disp_name,
                            category: d.category,
                        },
                    },
                    {
                        headers: { Authorization: `Bearer ${accessToken}` },
                    }
                );
            }

            await fetchDistributions();
        } catch (err) {
            console.error('Error saving distribution', err);
        }
    }, [activeAccount, apiRootUrl, fetchDistributions, getAccessToken]);

    // Delete a distribution row, then refresh the table
    const deleteDist = useCallback(async (d) => {
        if (!activeAccount) return;

        try {
            const accessToken = await getAccessToken();

            await axios.delete(
                `${apiRootUrl}/v1/distributions/${d.originalDistribution}/${d.originalEntity}`,
                {
                    headers: { Authorization: `Bearer ${accessToken}` },
                }
            );

            await fetchDistributions();
        } catch (err) {
            console.error('Error deleting distribution', err);
        }
    }, [activeAccount, apiRootUrl, fetchDistributions, getAccessToken]);

    // Refresh admin data when the signed-in account changes
    useEffect(() => {
        void fetchUsers();
        void fetchDistributions();
    }, [fetchUsers, fetchDistributions]);

    return {
        isAdmin,
        adminToken,
        users,
        setUsers,
        distributions,
        setDistributions,
        saveUser,
        deleteUser,
        saveDist,
        deleteDist,
    };
}